/**
 * An entity's Edit History, one page at a time (`GET /audit/v1/…/history`).
 *
 * TanStack `useInfiniteQuery`: the first page loads when `enabled` turns true
 * (e.g. the drawer opens); further pages load ONLY when the user presses
 * "Load more" (`fetchNextPage`) — never in a loop (G-C). Disabled outright in
 * an env where the audit service is not on V2 (`isAuditAvailable`).
 */
import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import { useInfiniteQuery, useQuery } from '@tanstack/vue-query';

import {
  DEFAULT_HISTORY_LIMIT,
  buildActorDirectory,
  getEntityHistory,
  isActorLookupAvailable,
  isAuditAvailable,
  isOpaqueUserId,
  listHistoryActors,
  shortId,
  shouldRetryHistoryRead,
  type HistoryAction,
  type HistoryActor,
  type HistoryEntry,
  type HistoryPage,
} from '@/api/services/audit';
import type { IamUser } from '@/api/services/iamUsers';

/**
 * Users & Roles' list key (`usersQueryKey` in
 * `modules/admin/composables/useIamUsers`), spelled out here so the shared
 * `ui` chunk doesn't pull in the admin module. A unit test pins the two equal.
 * @param employerId The employer.
 * @returns The key.
 */
export function historyActorsKey(employerId: string | null) {
  return ['iam-svc', 'users', employerId] as const;
}

/** Why a value is not shown. */
export type HistoryValueMask = 'hidden' | 'not-kept' | null;

/** One rendered table row: one changed attribute of one audit line. */
export interface HistoryRow {
  /** Stable row id. */
  id: string;
  at: string;
  actor: string;
  action: HistoryAction;
  /** The changed attribute ('' for a create/delete that lists none). */
  field: string;
  before: unknown;
  after: unknown;
  /** `hidden` = sensitive (caller-masked); `not-kept` = dropped by the server for size. */
  mask: HistoryValueMask;
}

/**
 * Query key for one entity's history.
 * @param employerId The employer.
 * @param entityKey The audited item's sort key.
 * @returns The key.
 */
export function entityHistoryKey(employerId: string, entityKey: string) {
  return ['audit', 'history', employerId, entityKey] as const;
}

/**
 * Whether a value carries nothing to show: `undefined` / `null` / `''` /
 * whitespace, an empty array or an empty plain object.
 * @param v The value.
 * @returns `true` when empty.
 */
export function isEmptyHistoryValue(v: unknown): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v as object).length === 0;
  return false;
}

/**
 * Storage internals a migration / create line can list — table keys, GSI
 * keys, the tenant partition, the migration checksum. They are not record
 * data (the user never sees or edits them), so they are never shown.
 */
const STORAGE_FIELD_RE =
  /^(?:PK|SK|GSI\d*(?:PK|SK)|Tenant|TenantId|EntityItem|EntityItemId|MigrationChecksum)$/;

/**
 * Whether `field` is a storage internal ({@link STORAGE_FIELD_RE}).
 * @param field The raw attribute name.
 * @returns `true` when the row should be hidden.
 */
export function isStorageHistoryField(field: string): boolean {
  return STORAGE_FIELD_RE.test(field);
}

/**
 * Bookkeeping attributes every write bumps (R2-F5): they say nothing the
 * row's own "When" column doesn't, so they are never shown as a change.
 */
const BOOKKEEPING_FIELDS: ReadonlySet<string> = new Set(['UpdatedAt', 'Version']);

/**
 * Whether `field` is per-write bookkeeping ({@link BOOKKEEPING_FIELDS}).
 * @param field The raw attribute name.
 * @returns `true` when the row should be hidden.
 */
export function isBookkeepingHistoryField(field: string): boolean {
  return BOOKKEEPING_FIELDS.has(field);
}

/**
 * Whether `v` is a plain (non-array, non-null) object.
 * @param v The value.
 * @returns `true` for a plain object.
 */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * For a reference field `<X>Id` / `<X>Ids`, the base field `<X>` — else null.
 * @param field The raw attribute name.
 * @returns The base name, or null.
 */
function refBaseField(field: string): string | null {
  const m = /^(.*[a-z0-9])Ids?$/.exec(field);
  return m ? m[1]! : null;
}

/**
 * Flatten audit lines into one row per changed attribute.
 *
 * Masking never shows a value: a field listed in `maskedFields`
 * (case-insensitive) is `hidden`; a field the server dropped for size
 * (`truncatedAttributes`, or absent from both sides on a truncated line) is
 * `not-kept`. Masked rows carry `undefined` before/after.
 *
 * A no-op line — an unmasked field that is empty both before and after
 * (e.g. "Learning paths — → —") — is dropped: it tells the reader nothing.
 * Storage internals (`PK`, `SK`, `MigrationChecksum`, …) are dropped too, as
 * are the per-write bookkeeping rows (`UpdatedAt`, `Version`). A reference
 * row `<X>Id` is dropped when the same line also carries `<X>` as an object
 * (the object row already names it — no duplicate "(id) → (id)" line).
 * @param entries The normalised audit lines, newest first.
 * @param maskedFields Attribute names whose values must never render.
 * @returns The rows, in order.
 */
export function flattenHistory(
  entries: readonly HistoryEntry[],
  maskedFields: readonly string[] = [],
): HistoryRow[] {
  const masked = new Set(maskedFields.map((f) => f.toLowerCase()));
  const rows: HistoryRow[] = [];
  entries.forEach((e, i) => {
    const base = `${e.at}|${e.requestId}|${i}`;
    const fields = e.changedAttributes.length > 0 ? e.changedAttributes : [''];
    const listed = new Set(e.changedAttributes);
    for (const field of fields) {
      if (field && (isStorageHistoryField(field) || isBookkeepingHistoryField(field))) continue;
      const refBase = field ? refBaseField(field) : null;
      if (
        refBase &&
        listed.has(refBase) &&
        (isPlainObject(e.before[refBase]) || isPlainObject(e.after[refBase]))
      ) {
        continue;
      }
      let mask: HistoryValueMask = null;
      if (field && masked.has(field.toLowerCase())) mask = 'hidden';
      else if (
        field &&
        (e.truncatedAttributes.includes(field) ||
          (e.truncated && !(field in e.before) && !(field in e.after)))
      ) {
        mask = 'not-kept';
      }
      if (
        field &&
        !mask &&
        isEmptyHistoryValue(e.before[field]) &&
        isEmptyHistoryValue(e.after[field])
      ) {
        continue;
      }
      rows.push({
        id: `${base}|${field}`,
        at: e.at,
        actor: e.actor,
        action: e.action,
        field,
        before: mask ? undefined : e.before[field],
        after: mask ? undefined : e.after[field],
        mask,
      });
    }
  });
  return rows;
}

/**
 * Render an attribute value as short text. `undefined`/`null`/'' → '—';
 * objects are compact JSON, capped at 200 characters.
 * @param v The value.
 * @returns Display text.
 */
export function formatHistoryValue(v: unknown): string {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  let s: string;
  try {
    s = JSON.stringify(v);
  } catch {
    s = String(v);
  }
  return s.length > 200 ? `${s.slice(0, 199)}…` : s;
}

// ---------------------------------------------------------------------------
// Field labels + value presentation (generic — no per-entity lookups)
// ---------------------------------------------------------------------------

/**
 * Attributes common to audited entities that ship a translated default label
 * under `v2.ui.edit-history.fields.<Name>`. A caller's `fieldLabels` always
 * wins; anything else falls back to {@link humanizeFieldName}.
 */
export const KNOWN_HISTORY_FIELDS = [
  'Name',
  'NameKm',
  'Code',
  'Title',
  'Description',
  'Status',
  'EmployerId',
  'ParentId',
  'OrgUnitId',
  'LocationId',
  'Version',
  'CreatedAt',
  'CreatedBy',
  'UpdatedAt',
  'UpdatedBy',
  'DeactivatedAt',
  'DeactivatedBy',
  'ReactivatedAt',
  'ReactivatedBy',
  'RemovedAt',
  'RemovedBy',
] as const;

const KNOWN_FIELD_SET: ReadonlySet<string> = new Set(KNOWN_HISTORY_FIELDS);

/**
 * Whether `field` has a translated default label.
 * @param field The raw attribute name.
 * @returns `true` when it is one of {@link KNOWN_HISTORY_FIELDS}.
 */
export function isKnownHistoryField(field: string): boolean {
  return KNOWN_FIELD_SET.has(field);
}

/**
 * Turn a raw attribute name into a readable label: split PascalCase /
 * camelCase / snake_case into words, sentence-case them (acronyms such as
 * `HC` kept), and drop a trailing `Id` / `Ids` of a reference field.
 * `OrgUnitId` → "Org unit", `HCBudget` → "HC budget",
 * `learning_paths` → "Learning paths".
 * @param field The raw attribute name.
 * @returns The label ('' for '').
 */
export function humanizeFieldName(field: string): string {
  if (!field) return '';
  const words = field
    .replace(/[_.-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/);
  if (words.length > 1 && /^Ids?$/.test(words[words.length - 1]!)) words.pop();
  return words
    .map((w, i) => {
      if (w.length > 1 && w === w.toUpperCase() && /[A-Z]/.test(w)) return w; // acronym
      const lower = w.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}

/** A reference attribute: `…Id` / `…Ids` (e.g. `LocationId`, `OrgUnitIds`). */
const REF_FIELD_RE = /[a-z0-9]Ids?$/;
/** An actor attribute: `…By` (e.g. `CreatedBy`, `DeactivatedBy`). */
const ACTOR_FIELD_RE = /[a-z0-9]By$/;
/** A timestamp attribute: `…At` (e.g. `UpdatedAt`). */
const TIME_FIELD_RE = /[a-z0-9]At$/;
/** An id-shaped value: `Type:…` / `TYPE#…`, a UUID, or 32 hex chars. */
const ID_VALUE_RE =
  /^(?:[A-Za-z][\w-]*[:#]\S+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i;
/** An ISO-8601 date or instant. */
const ISO_RE =
  /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * How one before/after value renders.
 * - `empty` → "—"
 * - `text`  → `text` verbatim
 * - `ref`   → a muted "(id)" (`title` carries the raw id for hover)
 * - `refs`  → a muted "(N ids)"
 * - `actor` → a resolved person (`text` = name or email; `title` = email)
 * - `date`  → `text` is the ISO value; the component formats it
 * - `system` → an automated actor (`…-bot`, `migration:…`); the component
 *   shows a translated "System" label (`title` keeps the raw id for hover)
 * - `pairs` → a flat map (e.g. a policy's changed values) as "Key: value"
 *   lines; a pair whose key is masked carries no value at all
 */
export type HistoryValueView =
  | { kind: 'empty' }
  | { kind: 'text'; text: string }
  | { kind: 'ref'; title: string }
  | { kind: 'refs'; count: number }
  | { kind: 'actor'; text: string; title: string }
  | { kind: 'system'; title: string }
  | { kind: 'date'; text: string }
  | { kind: 'pairs'; pairs: HistoryValuePair[] };

/** One "Key: value" line of a `pairs` view. */
export type HistoryValuePair =
  | { key: string; masked: true }
  | { key: string; masked: false; value: HistoryValueView };

/** Automated actors: a `…-bot` service principal or a `migration:…` run. */
const SYSTEM_ACTOR_RE = /^(?:.+[-_]bot|migration[:#].*)$/i;

/**
 * Whether a recorded actor is an automated system principal, not a person.
 * @param raw The recorded actor.
 * @returns `true` for `…-bot` / `…_bot` / `migration:…`.
 */
export function isSystemActor(raw: string): boolean {
  return SYSTEM_ACTOR_RE.test(raw.trim());
}

/**
 * Resolve an actor id for display: a known user → name (or email); a system
 * principal → `system`; an unknown opaque id → its short form; anything
 * already readable → as-is.
 * @param raw The recorded actor.
 * @param actors The actor directory.
 * @returns `actor` (resolved), `system`, `text`, or `empty`.
 */
export function describeActor(
  raw: string,
  actors: ReadonlyMap<string, HistoryActor>,
): Extract<HistoryValueView, { kind: 'actor' | 'system' | 'text' | 'empty' }> {
  const id = raw.trim();
  if (!id) return { kind: 'empty' };
  const hit = actors.get(id);
  if (hit && (hit.name || hit.email)) {
    return { kind: 'actor', text: hit.name || hit.email, title: hit.email };
  }
  if (isSystemActor(id)) return { kind: 'system', title: id };
  return { kind: 'text', text: isOpaqueUserId(id) ? shortId(id) : id };
}

/** Attributes that name an object for a human, in preference order. */
const HUMAN_NAME_KEYS = ['Name', 'LatinName', 'DisplayName', 'FullName', 'Title', 'Label'] as const;

/**
 * The human name of an object value (its `Name`, `LatinName`, … — see
 * {@link HUMAN_NAME_KEYS}), or null when it has none.
 * @param v The value.
 * @returns The trimmed name, or null.
 */
function humanNameOf(v: unknown): string | null {
  if (!isPlainObject(v)) return null;
  for (const k of HUMAN_NAME_KEYS) {
    const n = v[k];
    if (typeof n === 'string' && n.trim()) return n.trim();
  }
  return null;
}

/** Most keys a flat map may have to render as pairs (else compact JSON). */
const MAX_PAIRS = 20;

/**
 * Decide how to show one value of `field`. Generic, by attribute-name shape:
 * reference ids are never printed raw, actor ids (`…By`) resolve through
 * `actors`, timestamps (`…At`) are formatted by the caller.
 *
 * Objects (R2-F5): one with a human field (`Name`, `LatinName`, …) renders as
 * that name; an array of such objects as their names; a flat map of scalars
 * (e.g. a policy's `{"EarlyClockInWindow":2.25}`) as `pairs`, each value
 * described by its own key and a key in `maskedFields` never carrying its
 * value. Anything else stays compact JSON.
 * @param field The raw attribute name.
 * @param v The value.
 * @param actors The actor directory (may be empty).
 * @param maskedFields Keys (case-insensitive) whose nested values must never render.
 * @returns The view.
 */
export function describeHistoryValue(
  field: string,
  v: unknown,
  actors: ReadonlyMap<string, HistoryActor> = new Map(),
  maskedFields: readonly string[] = [],
): HistoryValueView {
  if (isEmptyHistoryValue(v)) return { kind: 'empty' };
  if (typeof v === 'string') {
    if (ACTOR_FIELD_RE.test(field)) return describeActor(v, actors);
    if (REF_FIELD_RE.test(field) && ID_VALUE_RE.test(v.trim())) return { kind: 'ref', title: v };
    if (TIME_FIELD_RE.test(field) && ISO_RE.test(v) && Number.isFinite(Date.parse(v))) {
      return { kind: 'date', text: v };
    }
  }
  if (
    Array.isArray(v) &&
    REF_FIELD_RE.test(field) &&
    v.every((x) => typeof x === 'string' && ID_VALUE_RE.test(x.trim()))
  ) {
    return { kind: 'refs', count: v.length };
  }
  const name = humanNameOf(v);
  if (name) return { kind: 'text', text: name };
  if (Array.isArray(v) && v.length > 0) {
    const names = v.map(humanNameOf);
    if (names.every((n): n is string => n !== null))
      return { kind: 'text', text: names.join(', ') };
  }
  if (isPlainObject(v)) {
    const entries = Object.entries(v).filter(([k]) => !isStorageHistoryField(k));
    const flat = entries.every(
      ([, x]) => x === null || ['string', 'number', 'boolean'].includes(typeof x),
    );
    if (flat && entries.length > 0 && entries.length <= MAX_PAIRS) {
      const masked = new Set(maskedFields.map((f) => f.toLowerCase()));
      return {
        kind: 'pairs',
        pairs: entries.map(
          ([key, x]): HistoryValuePair =>
            masked.has(key.toLowerCase())
              ? { key, masked: true }
              : { key, masked: false, value: describeHistoryValue(key, x, actors) },
        ),
      };
    }
  }
  return { kind: 'text', text: formatHistoryValue(v) };
}

/**
 * The opaque user ids an actor lookup would resolve: every line's actor plus
 * every `…By` value, deduplicated.
 * @param entries The loaded audit lines.
 * @returns The UUID-shaped ids.
 */
export function collectActorIds(entries: readonly HistoryEntry[]): string[] {
  const ids = new Set<string>();
  const add = (v: unknown) => {
    if (typeof v === 'string' && isOpaqueUserId(v.trim())) ids.add(v.trim());
  };
  for (const e of entries) {
    add(e.actor);
    for (const f of e.changedAttributes) {
      if (ACTOR_FIELD_RE.test(f)) {
        add(e.before[f]);
        add(e.after[f]);
      }
    }
  }
  return [...ids];
}

/**
 * The actor directory for one employer: ONE cached read of the V2 identity
 * user list, under Users & Roles' own query key (so either screen reuses the
 * other's data) — never a per-actor lookup (no N+1). It runs only when an
 * opaque id is on screen (`needed`), keeps the list for 30 minutes, and never
 * retries a 4xx: a user who may not list users just sees short ids.
 *
 * The signed-in user (`self`, from the JWT claims) is always resolvable
 * without any request — the most common actor on a record you just edited.
 * @param employerId The employer (reactive).
 * @param actorIds The opaque actor ids on screen (reactive).
 * @param self The signed-in user's id + display, if known (reactive).
 * @returns `actors` (id → actor; `self` always, others once loaded).
 */
export function useHistoryActors(
  employerId: MaybeRefOrGetter<string | null | undefined>,
  actorIds: MaybeRefOrGetter<readonly string[]>,
  self: MaybeRefOrGetter<({ id: string } & HistoryActor) | null> = null,
) {
  const lookupOn = isActorLookupAvailable();
  const unresolved = computed(() => {
    const me = toValue(self)?.id;
    return toValue(actorIds).some((id) => id !== me);
  });
  const query = useQuery<IamUser[], Error>({
    queryKey: computed(() => historyActorsKey(toValue(employerId) ?? null)),
    queryFn: () => listHistoryActors(toValue(employerId) as string),
    enabled: computed(() => lookupOn && !!toValue(employerId) && unresolved.value),
    staleTime: 30 * 60_000,
    retry: shouldRetryHistoryRead,
  });
  const actors = computed(() => {
    const dir = buildActorDirectory(query.data.value);
    const me = toValue(self);
    if (me?.id && (me.name || me.email) && !dir.has(me.id)) {
      dir.set(me.id, { name: me.name, email: me.email });
    }
    return dir;
  });
  return { actors, query };
}

/** Options for {@link useEntityHistory}. */
export interface UseEntityHistoryOptions {
  /** Load only while true (e.g. while the drawer is open). Defaults to true. */
  enabled?: MaybeRefOrGetter<boolean>;
  /** Page size (1–100). Defaults to 50. */
  limit?: number;
}

/**
 * One entity's history, paged by an explicit "Load more".
 * @param employerId The employer (reactive).
 * @param entityKey The audited item's sort key (reactive).
 * @param options `enabled` / `limit`.
 * @returns The infinite query plus `entries` (all loaded lines) and `available`.
 */
export function useEntityHistory(
  employerId: MaybeRefOrGetter<string | null | undefined>,
  entityKey: MaybeRefOrGetter<string | null | undefined>,
  options: UseEntityHistoryOptions = {},
) {
  const available = isAuditAvailable();
  const limit = options.limit ?? DEFAULT_HISTORY_LIMIT;

  const query = useInfiniteQuery({
    queryKey: computed(() => entityHistoryKey(toValue(employerId) ?? '', toValue(entityKey) ?? '')),
    queryFn: ({ pageParam }: { pageParam: string | undefined }): Promise<HistoryPage> =>
      getEntityHistory(toValue(employerId) as string, toValue(entityKey) as string, {
        cursor: pageParam,
        limit,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: HistoryPage) => last.nextCursor,
    enabled: computed(
      () =>
        available &&
        (options.enabled === undefined ? true : toValue(options.enabled)) &&
        !!toValue(employerId) &&
        !!toValue(entityKey),
    ),
    staleTime: 30_000,
    // Retry once, and only a network error or a 5xx — a 403/404 is final.
    retry: shouldRetryHistoryRead,
    retryDelay: 500,
  });

  const entries: ComputedRef<HistoryEntry[]> = computed(
    () => query.data.value?.pages.flatMap((p) => p.items) ?? [],
  );

  return { ...query, entries, available };
}
